import { useEffect, useState } from 'react';
import pitchesService, { PitchDetails } from '../../services/pitches';

type Entry = { pitches: PitchDetails[] | null; error: unknown; request: Promise<void> | null };

const entries = new Map<boolean, Entry>();
const listeners = new Set<() => void>();

const entryFor = (includeGenerated: boolean) => {
  let entry = entries.get(includeGenerated);
  if (!entry) {
    entry = { pitches: null, error: null, request: null };
    entries.set(includeGenerated, entry);
  }
  return entry;
};

const load = (includeGenerated: boolean) => {
  const entry = entryFor(includeGenerated);
  if (!entry.request) {
    entry.request = pitchesService
      .getPitches(includeGenerated)
      .then((pitches) => {
        entry.pitches = pitches;
        entry.error = null;
      })
      .catch((error) => {
        entry.error = error;
      })
      .finally(() => {
        entry.request = null;
        listeners.forEach((listener) => listener());
      });
  }
  return entry.request;
};

export const refreshPitches = () => Promise.all([...entries.keys()].map(load));

export default function usePitches(includeGenerated: boolean = false) {
  const [, rerender] = useState(0);

  useEffect(() => {
    const listener = () => rerender((n) => n + 1);
    listeners.add(listener);
    const entry = entryFor(includeGenerated);
    if (!entry.pitches && !entry.request) {
      load(includeGenerated);
    }
    return () => {
      listeners.delete(listener);
    };
  }, [includeGenerated]);

  const entry = entryFor(includeGenerated);
  return { pitches: entry.pitches, loading: !entry.pitches && !entry.error, error: entry.error };
}

export function usePitch(id?: string) {
  const [pitch, setPitch] = useState<PitchDetails | null>(null);

  useEffect(() => {
    let active = true;
    setPitch(null);
    if (id && id !== 'new') {
      pitchesService.getPitch(id).then((result) => active && setPitch(result), () => undefined);
    }
    return () => {
      active = false;
    };
  }, [id]);

  return pitch;
}
